import { SanctionRecommendation } from '@/interfaces/infractions';
import { InfractionService } from '@/services/infraction/infraction.service';
import { PersonService } from '@/services/person/person.service';
import { StoreState } from '@/store';
import { modalShow } from '@/store/modal/actions';
import { loaderAnimationHide, loaderAnimationShow } from '@/store/page-loader/action';
import { ofuscate, deofuscate } from '@/utils/obfuscation';
import { dateEndMustBeAfterDateStart } from '@/validators/dateEndMustBeAfterDateStart';
import { dateEndSettedOrUndefined } from '@/validators/dateEndSettedOrUndefined';
import { dateMustBeAfterOrEqualThanToday } from '@/validators/dateMustBeAfterOrEqualThanToday';
import { dateMustBeBeforeOrEqualThanToday } from '@/validators/dateMustBeBeforeOrEqualThanToday';
import { mustHaveOneRecord } from '@/validators/mustHaveOneRecord';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { delay } from 'rxjs/operators';

@Component({
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.css'],
})
export class FormComponent implements OnInit {

  form: FormGroup = new FormGroup({
    person: new FormGroup({
      id: new FormControl(0, [Validators.required, Validators.min(1)]),
      email: new FormControl(''),
      mdmid: new FormControl('', [Validators.required]),
    }),
    locationAndEquipment: new FormGroup({
      assetId: new FormControl('', [Validators.required, Validators.min(1)]),
      zoneId: new FormControl('', [Validators.required, Validators.min(1)]),
      vehicleTypeId: new FormControl('', [Validators.required, Validators.min(1)]),
      patent: new FormControl('', [Validators.required]),
    }),
    detail: new FormGroup({
      day: new FormControl('', [Validators.required, dateMustBeBeforeOrEqualThanToday()]),
      hour: new FormControl(''), // now is optional
      personResponsibleId: new FormControl(0), // now is optional
      severityTypeId: new FormControl(0, [Validators.required, Validators.min(1)]),
      infrigmentTypeId: new FormControl(0, [Validators.required, Validators.min(1)]),
      observation: new FormControl(''),
      reporterTypeId: new FormControl(0), // now is optional
      reporterPersonId: new FormControl(0, [Validators.required, Validators.min(0)]),
      evidenceTypes: new FormControl([]),
      witnesses: new FormControl([]),
    }),
    sanctionType: new FormGroup({
      sanctionTypeId: new FormControl('', [Validators.required, Validators.min(1)]),
      suspensionDays: new FormControl('', [Validators.required]),
      sanctionRange: new FormGroup({
        start: new FormControl(null, [dateEndSettedOrUndefined()]),
        end: new FormControl(null, [dateEndSettedOrUndefined(), dateMustBeAfterOrEqualThanToday()]),
      }, [dateEndMustBeAfterDateStart('start', 'end')]),
      expirationDate: new FormControl(null, [dateEndSettedOrUndefined()]),
    }),
  });

  severityName: string = '';
  infrigementName: string = '';
  recommendation?: SanctionRecommendation;

  get formGroupPerson(): FormGroup {
    const personForm = this.form.controls.person as FormGroup;
    return personForm;
  }
  get formControlPersonId(): FormControl {
    const personForm = this.form.controls.person as FormGroup;
    return personForm.controls.id as FormControl;
  }
  get formGroupLocationAndEquipment(): FormGroup {
    const locationAndEquipmentForm = this.form.controls.locationAndEquipment as FormGroup;
    return locationAndEquipmentForm;
  }
  get formGroupDetail(): FormGroup {
    const detailForm = this.form.controls.detail as FormGroup;
    return detailForm;
  }
  get formGroupSanctionType(): FormGroup {
    const sanctionType = this.form.controls.sanctionType as FormGroup;
    return sanctionType;
  }
  get formPersonIdControl(): FormControl {
    const personForm = this.form.controls.person as FormGroup;
    return personForm.controls.id as FormControl;
  }
  get assetId(): number {
    const locationAndEquipmentForm = this.form.controls.locationAndEquipment as FormGroup;
    return locationAndEquipmentForm.controls.assetId.value;
  }

  constructor(
    private store: Store<StoreState>,
    private router: Router,
    private personService: PersonService,
    private infractionService: InfractionService,
    private route: ActivatedRoute,
  ) {

    const requestRecomendation$ = combineLatest([
      (<FormGroup>this.form.controls.detail).controls.severityTypeId.valueChanges,
      (<FormGroup>this.form.controls.detail).controls.infrigmentTypeId.valueChanges,
    ]);

    requestRecomendation$.subscribe({
      next: ([severityTypeId, infrigmentTypeId]) => {
        if (severityTypeId && infrigmentTypeId) {
          if (+severityTypeId > 0 && +infrigmentTypeId) {
            // This locks the sanction form until the recommendation is fetched.
            this.recommendation = undefined;
            const { mdmid } = this.form.value.person;
            this.infractionService.getInfringementsQuantityByUser(mdmid).subscribe((infractionQuantity) => {
              const { assetId } = this.form.value.locationAndEquipment;
              const infractionQuantityPlus = infractionQuantity + 1;
              this.infractionService.getRecomendation(assetId, +severityTypeId, infractionQuantityPlus).subscribe({
                next: (value) => {
                  const currentInfrigmentTypeId = (<FormGroup>this.form.controls.detail).controls.infrigmentTypeId.value;
                  // if the value change while fetching data, dont save the response
                  if (infrigmentTypeId !== currentInfrigmentTypeId) {
                    return;
                  }
                  this.recommendation = value;
                },
              });
            });
          }
          else {
            this.recommendation = undefined;
          }
        }
      },
    });
  }

  ngOnInit() {
    this.route.params.pipe().subscribe(({ ofuscatedData }) => {
      const params = deofuscate(ofuscatedData);
      if (params) {
        this.formGroupPerson.patchValue({
          id: params.personId,
        });
        this.personService.getPersonDataById(params.personId).subscribe({
          next: (personData) => {
            this.formGroupPerson.patchValue({
              id: personData.id,
              email: personData.email,
              mdmid: personData.mdmid,
            });
          },
        });
      }
    });
  }

  onCancel() {
    this.store.dispatch(modalShow({
      title: '¿Estas seguro de enviar esta solicitud?',
      text: `
        La infracción será cancelada automáticamente si continúa en cancelar infracción. Presiona Volver para regresar a la pantalla anterior.
      `,
      acceptButtonText: 'Cancelar infracción',
      actionOnAccept: () => {
        this.form.reset();
        this.onResetForm();
      },
    }));
  }

  onSubmit() {
    this.store.dispatch(modalShow({
      title: '¿Estas seguro de que quieres ingresar la información?',
      text: `
        Si la información está correcta, continúa en registrar infracción.
      `,
      text2: 'De lo contrario, presiona  Volver para revisar la información.',
      acceptButtonText: 'Registrar infracción',
      actionOnAccept: () => {
        this.store.dispatch(loaderAnimationShow());

        of({}).pipe(delay(3000)).subscribe({
          next: () => {
            const formValues = this.form.value;

            const dateTimeInfringement = <Date>formValues.detail.day;

            // Corrige el desface por la zona horaria
            dateTimeInfringement.setHours(dateTimeInfringement.getHours() - (dateTimeInfringement.getTimezoneOffset() / 60));

            if (formValues.detail.hour) {
              const [hour, min] = formValues.detail.hour.split(':');
              dateTimeInfringement.setMinutes(+min);
              dateTimeInfringement.setHours(hour - (dateTimeInfringement.getTimezoneOffset() / 60));
            }

            const selectedPersonId = formValues.person.id;
            const responsableId = formValues.detail.personResponsibleId;
            const reporterId = formValues.detail.reporterPersonId;
            let witnessesIds: number[] = formValues.detail.witnesses; // vector
            witnessesIds = witnessesIds.filter((witness) => witness > 0);
            const observables = [];
            if (selectedPersonId > 0) {
              observables.push(this.personService.getPersonDataById(selectedPersonId));
            }
            else {
              observables.push(of({ mdmid: undefined }));
            }
            if (responsableId > 0) {
              observables.push(this.personService.getPersonDataById(responsableId));
            }
            else {
              observables.push(of({ mdmid: undefined }));
            }
            if (reporterId > 0) {
              observables.push(this.personService.getPersonDataById(reporterId));
            }
            else {
              observables.push(of({ mdmid: undefined }));
            }
            if (witnessesIds.length > 0) {
              witnessesIds.map((id) => observables.push(this.personService.getPersonDataById(id)));
            }

            const persons$ = combineLatest(observables);

            persons$.subscribe({
              next: ([selectedPerson, responsable, reporter, ...witnesses]) => {
                const outt: Record<string, any> = {
                  assetId: +formValues.locationAndEquipment.assetId,
                  zoneId: +formValues.locationAndEquipment.zoneId,
                  equipmentId: formValues.locationAndEquipment.vehicleTypeId,
                  equipmentPatent: formValues.locationAndEquipment.patent,
                  dateTimeInfringement,
                  responsableMdmid: responsable.mdmid,
                  severityId: formValues.detail.severityTypeId,
                  infringementTypeId: formValues.detail.infrigmentTypeId,
                  observation: formValues.detail.observation,
                  infringementReportId: formValues.detail.reporterTypeId,
                  infringementReportMdmid: reporter.mdmid,
                  sanctionTypeId: +formValues.sanctionType.sanctionTypeId,
                  suspensionDaysId: formValues.sanctionType.suspensionDays,
                  dateStart: formValues.sanctionType.sanctionRange.start || formValues.sanctionType.expirationDate,
                  dateEnd: formValues.sanctionType.sanctionRange.end,
                  status: 1,
                  driverMdmid: selectedPerson.mdmid,
                  infringementEvidence: (formValues.detail.evidenceTypes as number[]).map((id) => ({ evidenceId: id })),
                  infringementWitness: witnesses.map((witness) => ({
                    witnessMdmid: witness.mdmid,
                  })),
                };

                const cleanedOut = Object.keys(outt).filter((key) => {
                  const value = outt[key];
                  if (value) {
                    if (!Number.isNaN(+value)) {
                      if (value >= 1) {
                        return true;
                      }
                    }
                    else {
                      return true;
                    }
                  }
                  return false;
                }).reduce<Record<string, any>>((obj, key) => {
                  // eslint-disable-next-line no-param-reassign
                  obj[key] = outt[key];
                  return obj;
                }, {});

                this.infractionService.createInfringement(cleanedOut).subscribe({
                  next: ({ id }) => {
                    const ofuscatedData = ofuscate({
                      email: this.form.value.person.email || 'SIN EMAIL', infractionId: id,
                    });
                    this.store.dispatch(loaderAnimationHide());
                    this.router.navigateByUrl(`/infractions/enter/success/${ofuscatedData}`);
                  },
                  error: () => {
                    this.store.dispatch(loaderAnimationHide());

                    this.store.select('pageError').subscribe({
                      next: (pageConfig) => {
                        const [website, ...url] = window.location.href.split('//')[1].split('/');
                        if (pageConfig.errorType !== 'Mulesoft') {

                          this.router.navigateByUrl('/infractions/enter/failure');

                        }
                      },
                    });

                  },
                });

              },
              error: () => {
                this.store.dispatch(loaderAnimationHide());

                this.store.select('pageError').subscribe({
                  next: (pageConfig) => {
                    const [website, ...url] = window.location.href.split('//')[1].split('/');
                    if (pageConfig.errorType !== 'Mulesoft') {

                      this.router.navigateByUrl('/infractions/enter/failure');

                    }
                  },
                });


              },
            });
          },
          error: () => {
            this.store.dispatch(loaderAnimationHide());

            this.store.select('pageError').subscribe({
              next: (pageConfig) => {
                const [website, ...url] = window.location.href.split('//')[1].split('/');
                if (pageConfig.errorType !== 'Mulesoft') {
                  this.router.navigateByUrl('/infractions/enter/failure');
                }
              },
            });
          },
        });
      },
    }));
  }

  onChangeSeverity(name: string) {
    this.severityName = name;
  }

  onChangeInfringement(name: string) {
    this.infrigementName = name;
  }

  onResetForm() {
    this.form.controls.locationAndEquipment.patchValue({
      assetId: '',
    });
  }
}
